import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import {Lock, Plus} from "lucide-react";

import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, showToast, formatDateTime} from "../../utils/utility";
import {buildPaginationParams, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS} from "../../utils/paginationUtils";
import {sessionStatusMeta, extractErrorMessage} from "./scheduledUnlockMeta";
import useScheduledUnlockUpdates from "../../hooks/useScheduledUnlockUpdates";

import Button from "../../components/ui/Button";
import Pill from "../../components/ui/Pill";
import Spinner from "../../components/ui/Spinner";
import EmptyState from "../../components/ui/EmptyState";
import PageHeader from "../../components/layout/PageHeader";
import BackToDashboardButton from "../../components/ui/BackToDashboardButton";
import {tableStyles} from "../../components/data/dataTableTheme";
import CreateScheduledUnlockSlideOver from "./CreateScheduledUnlockSlideOver";
import ScheduledUnlockDetailSlideOver from "./ScheduledUnlockDetailSlideOver";

/**
 * Scheduled Group Unlock — list of sessions for this client, each a batch
 * of GPS locks scheduled to unlock together at a future UTC time.
 */
export default function ScheduledUnlockList() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [detailSessionId, setDetailSessionId] = useState(null);
  const [editSession, setEditSession] = useState(null);
  // Lock(s) to pre-select in the create form when arriving from a device's
  // "Schedule Unlock" action on the dashboard (router state).
  const [presetUnitIds, setPresetUnitIds] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  // Opened from a device's "Schedule Unlock" button — auto-open the create form
  // pre-selected on that lock, then clear the router state so a refresh/back
  // doesn't reopen it.
  useEffect(() => {
    // Single lock (device details "Schedule Unlock") or many (dashboard bulk select).
    const many = location.state?.presetAssetUnitIds;
    const one = location.state?.presetAssetUnitId;
    const ids = Array.isArray(many) && many.length ? many : (one ? [one] : []);
    if (ids.length) {
      setPresetUnitIds(ids);
      setCreateOpen(true);
      navigate(location.pathname, {replace: true, state: null});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessions = async (page, size) => {
    try {
      setLoading(true);
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.SCHEDULED_UNLOCKS}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      // Backend returns a raw Spring Page — {content, totalElements, ...} —
      // not the {responseData: …} envelope used by the older endpoints.
      const res = await axios.get(url, {params: buildPaginationParams(page, size)});
      setSessions(res.data?.content || []);
      setTotalRows(res.data?.totalElements ?? (res.data?.content || []).length);
      setCurrentPage(page);
    } catch (err) {
      showToast(extractErrorMessage(err, "Could not load scheduled unlocks"), {type: "error"});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => fetchSessions(1, perPage);

  // Live updates: patch a row in place if it's on the current page; a brand-new session only
  // gets inserted client-side when viewing page 1 (newest-first), since we can't know which
  // page it belongs to otherwise — anything else is left for the next fetch to pick up.
  useScheduledUnlockUpdates((data) => {
    setSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === data.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = {...next[idx], ...data};
        return next;
      }
      if (currentPage === 1) {
        setTotalRows((t) => t + 1);
        return [data, ...prev].slice(0, perPage);
      }
      return prev;
    });
  }, true);

  const columns = [
    {
      name: "Session",
      grow: 1.6,
      minWidth: "220px",
      cell: (row) => (
        <div className="min-w-0">
          <div className="font-medium text-ink-900 truncate">{row.sessionName || `Session #${row.id}`}</div>
          <div className="text-xs text-ink-500 truncate">by {row.createdByUsername}</div>
        </div>
      ),
    },
    {
      name: "Locks",
      width: "160px",
      center: true,
      cell: (row) => (
        <span className="text-sm text-ink-700">
          <span className="font-semibold text-ink-900">{row.successCount}</span>
          {" / "}
          {row.totalLockCount}
          {row.failedCount > 0 && <span className="text-danger-600"> ({row.failedCount} failed)</span>}
        </span>
      ),
    },
    {
      name: "Scheduled for",
      width: "200px",
      sortable: true,
      selector: (row) => row.scheduledAtUtc,
      cell: (row) => (
        <span className="text-sm text-ink-700" title={`${row.requestedLocalTime} (${row.requestedTimezone})`}>
          {formatDateTime(row.scheduledAtUtc) || "—"}
        </span>
      ),
    },
    {
      name: "Status",
      width: "170px",
      cell: (row) => {
        const meta = sessionStatusMeta(row.status);
        return <Pill tone={meta.tone} size="sm">{meta.label}</Pill>;
      },
    },
    {
      name: "",
      width: "100px",
      right: true,
      cell: (row) => (
        <Button variant="tertiary" size="sm" onClick={() => setDetailSessionId(row.id)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Scheduled Unlocks"
        subtitle="Schedule a group of GPS locks to unlock at a future date and time."
        actions={
          <>
            <BackToDashboardButton />
            <Button variant="primary" icon={Plus} onClick={() => setCreateOpen(true)}>
              Schedule unlock
            </Button>
          </>
        }
      />

      <div className="px-4 lg:px-6 py-4 space-y-4">
        <DataTable
          columns={columns}
          data={sessions}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onChangePage={(page) => fetchSessions(page, perPage)}
          onChangeRowsPerPage={(size, page) => { setPerPage(size); fetchSessions(page, size); }}
          onRowClicked={(row) => setDetailSessionId(row.id)}
          responsive
          highlightOnHover
          pointerOnHover
          progressPending={loading}
          progressComponent={
            <div className="py-12 flex items-center justify-center gap-3 text-ink-500">
              <Spinner size="md" /><span className="text-sm">Loading scheduled unlocks…</span>
            </div>
          }
          noDataComponent={
            <EmptyState
              icon={Lock}
              title="No scheduled unlocks yet"
              description="Schedule a group of GPS locks to unlock together at a future date and time."
              action={
                <Button variant="primary" icon={Plus} onClick={() => { setPresetUnitIds([]); setCreateOpen(true); }}>
                  Schedule unlock
                </Button>
              }
            />
          }
          keyField="id"
          customStyles={tableStyles}
        />
      </div>

      <CreateScheduledUnlockSlideOver
        open={createOpen || !!editSession}
        sessionToEdit={editSession}
        presetUnitIds={presetUnitIds}
        onClose={() => { setCreateOpen(false); setEditSession(null); setPresetUnitIds([]); }}
        onCreated={refresh}
      />

      <ScheduledUnlockDetailSlideOver
        sessionId={detailSessionId}
        open={!!detailSessionId}
        onClose={() => setDetailSessionId(null)}
        onChanged={refresh}
        onEdit={(session) => { setEditSession(session); setDetailSessionId(null); }}
      />
    </>
  );
}
